import React, { useCallback } from 'react';
import { Button, ControlLink, Text, Stack } from '@moda/om';
import { hrefFor } from '../../routers';
import { Return } from '../../hooks/useReturn';
import { useReturns } from '../../hooks/useReturns';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { AccountPage } from '../../pages/AccountPage';
import { Grid } from '../Grid';
import { Loading } from '../Loading';
import { AccountReturnsDescription } from './AccountReturnsDescription';
import { AccountReturnOrder } from './AccountReturnOrder';

import './AccountReturns.scss';

export const AccountReturns: React.FC = () => {
  const {
    returns,
    returnOrderItems,
    error: returnsError,
    fetchMore,
    pagination,
    loading
  } = useReturns();

  if (returnsError) throwQueryError(returnsError);

  const getItemsForReturn = useCallback(
    (returnOrder: Return) => {
      const ids = returnOrder.relationships.returnOrderItems.data.map(item => item.id) || [];
      return returnOrderItems?.filter(item => ids.includes(item.id));
    },
    [returnOrderItems]
  );

  if (!returns)
    return (
      <AccountPage title="Returns">
        <Loading />
      </AccountPage>
    );

  const nextPage = pagination?.nextPage;
  const totalItems = pagination?.totalEntries;

  return (
    <AccountPage title="Returns" description={<AccountReturnsDescription />}>
      <Stack space={4}>
        {returns?.map(returnOrder => (
          <AccountReturnOrder
            key={returnOrder.id}
            returnOrder={returnOrder}
            items={getItemsForReturn(returnOrder)}
          />
        ))}

        {nextPage && (
          <Grid className="AccountReturns__footer">
            <Button data-testid="LoadMore" onClick={() => fetchMore(nextPage)} disabled={loading}>
              Load More
            </Button>
            <Stack alignItems="center" direction="horizontal" justifyContent="flex-end" space={0}>
              <Text data-testid="Showing">
                {`SHOWING 1-${returns.length} OF ${totalItems} RETURNS`}
              </Text>
            </Stack>
          </Grid>
        )}

        {returns?.length === 0 && (
          <Text treatment="body1">
            You currently do not have any returns. To start a return, please visit the{' '}
            <ControlLink to={hrefFor.AccountOrderHistory()}>Order History Page</ControlLink>.
          </Text>
        )}
      </Stack>
    </AccountPage>
  );
};
